import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SoldOutBadge } from "@/components/location/SoldOutBadge";
import { useProductActionState } from "@/hooks/useProductAvailability";
import { useLocationContext } from "@/contexts/LocationContext";
import { canonicalHandle } from "@/lib/availability";
import { cn } from "@/lib/utils";

interface CartItemAvailabilityWarningProps {
  productHandle: string | null | undefined;
  basePrice?: number | null;
  className?: string;
}

/**
 * Calm, helpful item-level message shown under a cart line when the product
 * isn't purchasable at the selected location. Offers clear next steps instead
 * of a harsh error, and stays silent for normal available products.
 */
export function CartItemAvailabilityWarning({
  productHandle,
  basePrice = null,
  className,
}: CartItemAvailabilityWarningProps) {
  const { openSelector } = useLocationContext();
  const { behaviour } = useProductActionState(canonicalHandle(productHandle), basePrice);

  if (behaviour.canAddToCart) return null;

  const text = behaviour.isSoldOut
    ? "This item isn't available at your selected location right now. You can check another branch or request a stock update."
    : "This item needs a quick confirmation for your selected location before checkout — our team can help.";

  return (
    <div className={cn(
      "mt-1.5 rounded-md border p-2 text-xs",
      behaviour.isSoldOut ? "border-destructive/30 bg-destructive/5" : "border-accent/30 bg-accent/5",
      className,
    )}>
      {behaviour.isSoldOut && <SoldOutBadge className="mb-1" />}
      <p className="flex items-start gap-1.5 text-foreground/80">
        <AlertTriangle className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", behaviour.isSoldOut ? "text-destructive" : "text-accent-foreground")} />
        {text}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={openSelector}>
          Check another location
        </Button>
        <Button asChild variant="outline" size="sm" className="h-7 text-xs">
          <Link to="/contact">{behaviour.isSoldOut ? "Request stock update" : "Contact branch"}</Link>
        </Button>
      </div>
    </div>
  );
}
